/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Department } from '../models/Department';
import type { DepartmentRequest } from '../models/DepartmentRequest';
import type { PaginatedDepartmentList } from '../models/PaginatedDepartmentList';
import type { PatchedDepartmentRequest } from '../models/PatchedDepartmentRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DepartmentsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint to view and edit company departments
     * @returns PaginatedDepartmentList
     * @throws ApiError
     */
    public companiesDepartmentsList({
        parentCompany,
        limit,
        offset,
    }: {
        parentCompany: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedDepartmentList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/departments/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * API endpoint to view and edit company departments
     * @returns Department
     * @throws ApiError
     */
    public companiesDepartmentsCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: DepartmentRequest,
    }): CancelablePromise<Department> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/departments/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company departments
     * @returns Department
     * @throws ApiError
     */
    public companiesDepartmentsRetrieve({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this department.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<Department> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/departments/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * API endpoint to view and edit company departments
     * @returns Department
     * @throws ApiError
     */
    public companiesDepartmentsUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this department.
         */
        id: string,
        parentCompany: string,
        requestBody: DepartmentRequest,
    }): CancelablePromise<Department> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{parent_company}/departments/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company departments
     * @returns Department
     * @throws ApiError
     */
    public companiesDepartmentsPartialUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this department.
         */
        id: string,
        parentCompany: string,
        requestBody?: PatchedDepartmentRequest,
    }): CancelablePromise<Department> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{parent_company}/departments/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company departments
     * @returns void
     * @throws ApiError
     */
    public companiesDepartmentsDestroy({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this department.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/departments/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

}
